/**
 * Jul-2026 (User-Wunsch) — Einklappbare Live-Vorschau des Dokument-Rahmens in
 * den Unternehmensdaten-Einstellungen. Spiegelt exakt das generierte PDF
 * (pdf_branding.make_legal_footer_canvas):
 *   • Kopfzeile: Logo links (ohne Firmennamen) · Dokumentname rechts.
 *   • Fußzeile: 4 Spalten (Firma | Sitz der Gesellschaft |
 *     Geschäftsführer/HR | USt/Steuer/Bank).
 * Markiert fehlende Pflichtangaben.
 */
import { useState } from "react";
import { Eye, CheckCircle2, AlertCircle, Bike, ChevronDown } from "lucide-react";

// Pflichtangaben; USt-IdNr. UND Steuernr. gelten als EINE Anforderung (entweder-oder),
// konsistent mit dem Dokument-Check.
const FOOTER_FIELDS = [
    { keys: ["company_name", "name"], label: "Firma" },
    { keys: ["address", "street"], label: "Anschrift" },
    { keys: ["company_seat"], label: "Sitz der Gesellschaft" },
    { keys: ["managing_director"], label: "Geschäftsführer" },
    { keys: ["register_number", "handelsregister"], label: "Handelsregisternummer" },
    { keys: ["register_court"], label: "Registergericht" },
    { keys: ["ust_id", "vat_id", "ustid", "tax_number", "tax_id"], label: "USt-IdNr. / Steuernr." },
    { keys: ["bank_name"], label: "Bank" },
    { keys: ["iban", "bank_iban"], label: "IBAN" },
    { keys: ["bic", "bank_bic"], label: "BIC" },
];

function Col({ heading, lines }) {
    const visible = lines.filter(Boolean);
    return (
        <div className="min-w-0">
            {heading ? <div className="text-[9px] font-bold uppercase tracking-wider text-gray-600 mb-0.5">{heading}</div> : null}
            {visible.length
                ? visible.map((ln, i) => <div key={i} className="text-[10px] text-gray-500 leading-tight truncate">{ln}</div>)
                : <div className="text-[10px] text-gray-300 italic">—</div>}
        </div>
    );
}

export default function FooterPreviewCard({ currentUser }) {
    const [open, setOpen] = useState(true);
    const [missingOpen, setMissingOpen] = useState(true);
    const d = currentUser || {};
    const g = (...keys) => { for (const k of keys) { const v = String(d[k] ?? "").trim(); if (v) return v; } return ""; };
    const has = (keys) => keys.some((k) => String(d[k] ?? "").trim());
    const missing = FOOTER_FIELDS.filter((f) => !has(f.keys));

    const company = g("company_name", "name");
    const addr = g("address", "street");
    const plzOrt = [g("zip", "postal_code"), g("city")].filter(Boolean).join(" ");
    const country = g("country");
    // Sitz sauber: gepflegte Sitz-Adresse ODER company_seat unverändert ODER Stadt.
    const seatLines = g("seat_address")
        ? [g("seat_address"), [g("seat_zip", "zip", "postal_code"), g("company_seat", "city")].filter(Boolean).join(" ")]
        : g("company_seat") ? [g("company_seat")]
        : g("city") ? [g("city")] : [];
    const gf = g("managing_director");
    let regNo = g("register_number", "handelsregister");
    if (regNo && !/^(HRB|HRA|GNR|VR|PR)/i.test(regNo)) regNo = `HRB ${regNo}`;
    const regCourt = g("register_court"); // enthält bereits „Amtsgericht …" → kein Zusatz-Präfix
    const ust = g("ust_id", "vat_id", "ustid");
    const tax = g("tax_number", "tax_id");
    const bank = g("bank_name");
    const iban = g("iban", "bank_iban");
    const bic = g("bic", "bank_bic");
    const logoUrl = g("company_logo_url", "logo_url");

    const columns = [
        { heading: null, lines: [company, addr, plzOrt, country] },
        { heading: "Sitz der Gesellschaft", lines: [...seatLines, regNo, regCourt] },
        { heading: "Geschäftsführer", lines: [gf] },
        { heading: null, lines: [ust && `USt-IdNr.: ${ust}`, tax && `Steuernr.: ${tax}`, bank, iban && `IBAN: ${iban}`, bic && `BIC: ${bic}`] },
    ];

    return (
        <div className="border-b border-gray-200" data-testid="footer-preview-card">
            {/* Einklappbare Überschrift */}
            <div className="flex items-center bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex-1 flex items-center gap-3 px-5 py-3 text-left min-w-0"
                data-testid="footer-preview-toggle"
                aria-expanded={open}
            >
                <Eye className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#111827] flex items-center gap-2">
                        <span className="truncate">Dokument-Vorschau (Kopf- &amp; Fußzeile)</span>
                        {missing.length > 0 && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 shrink-0">{missing.length} fehlt</span>
                        )}
                    </div>
                    <div className="text-[11px] text-gray-500">So erscheinen Kopf- &amp; Fußzeile auf Rechnungen, Verträgen &amp; Dokumenten.</div>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${open ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            </div>

            {open && (
                <div className="px-5 py-4">
                    <p className="text-[11px] text-gray-500 mb-3">
                        Fehlende Angaben unten ergänzen (bei Filialen: am Hauptsitz eintragen &amp; „Org-weit"-Schalter aktivieren).
                    </p>

                    <div className="border border-gray-200 rounded-sm bg-white shadow-sm overflow-hidden" data-testid="footer-preview-render">
                        <div className="flex items-center justify-between px-4 py-2.5 border-b-2 border-gray-200" data-testid="footer-preview-header">
                            {logoUrl
                                ? <img src={logoUrl} alt="Logo" className="h-7 max-w-[140px] object-contain" />
                                : <div className="w-7 h-7 rounded-md bg-[#FF8300] flex items-center justify-center"><Bike className="w-4 h-4 text-black" /></div>}
                            <span className="text-sm font-black uppercase tracking-wider text-[#111827]">RECHNUNG</span>
                        </div>
                        <div className="px-4 py-6 bg-gray-50/50 text-center text-[10px] text-gray-300">Dokumentinhalt …</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 px-4 py-2.5 border-t border-gray-200" data-testid="footer-preview-columns">
                            {columns.map((c, i) => <Col key={i} heading={c.heading} lines={c.lines} />)}
                        </div>
                    </div>

                    <div className="mt-3">
                        {missing.length === 0 ? (
                            <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700" data-testid="footer-preview-complete">
                                <CheckCircle2 className="w-4 h-4" /> Alle Pflichtangaben vorhanden.
                            </div>
                        ) : (
                            <div data-testid="footer-preview-missing">
                                <button
                                    type="button"
                                    onClick={() => setMissingOpen((o) => !o)}
                                    className="w-full flex items-center gap-1.5 text-xs font-bold text-amber-700 mb-1.5"
                                    aria-expanded={missingOpen}
                                    data-testid="footer-missing-toggle"
                                >
                                    <AlertCircle className="w-4 h-4 shrink-0" /> Fehlende Angaben ({missing.length}):
                                    <ChevronDown className={`w-3.5 h-3.5 ml-auto shrink-0 transition-transform ${missingOpen ? "rotate-180" : ""}`} />
                                </button>
                                {missingOpen && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {missing.map((f) => (
                                            <span key={f.label} className="text-[11px] px-2 py-0.5 rounded-sm bg-amber-50 border border-amber-300 text-amber-800 font-medium">
                                                {f.label}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
